const ALLOW = { ok: true };
function refuse(code, reason) {
    return { ok: false, code, reason };
}
/** Table row 1: read the user list. Admin, including the SuperAdmin (always admin too). */
export function canListUsers(actor) {
    if (!actor.admin) {
        return refuse('not-admin', 'Reading the staff list requires an admin.');
    }
    return ALLOW;
}
/**
 * Table row 2: change anyone's role, except the SuperAdmin's own — which only
 * the SuperAdmin may change, and only for themselves. `actor.superAdmin` is
 * checked rather than trusting `actor.id === target.id` alone: identity match
 * here already implies SuperAdmin (there is only ever one `superAdmin: true`
 * row, and target.superAdmin is true), but stating the authority check
 * explicitly is what makes this branch readable as "the SuperAdmin, acting on
 * themselves" rather than "whoever happens to share this id".
 */
export function canChangeRole(actor, target) {
    if (target.superAdmin) {
        if (actor.superAdmin && actor.id === target.id)
            return ALLOW;
        return refuse('super-admin-role-locked', "Only the SuperAdmin may change the SuperAdmin's own role.");
    }
    // Role stopped being self-service the moment this route came online
    // (design §1 decision 1) — an admin changing THEIR OWN role still goes
    // through this same admin check, not a carve-out; the only carve-out in
    // this whole table belongs to the SuperAdmin, above.
    if (!actor.admin) {
        return refuse('not-admin', 'Changing a role requires an admin.');
    }
    return ALLOW;
}
/**
 * Table row 3: grant or revoke the admin flag. SuperAdmin only, no exception —
 * not even "you can grant it to yourself", which cannot arise anyway since
 * nobody but the SuperAdmin already has `superAdmin: true`.
 */
export function canChangeAdmin(actor, target) {
    if (!actor.superAdmin) {
        return refuse('not-super-admin', 'Only the SuperAdmin may grant or revoke admin.');
    }
    // The SuperAdmin's own admin flag never moves, including by their own hand.
    // `super_admin: true, admin: false` is a contradiction migration 013's backfill
    // refuses to create (design §2) — this route must refuse to create it too,
    // rather than let the one person who could always overrule this check talk
    // themselves into the one state the rest of the model cannot express.
    if (target.superAdmin) {
        return refuse('super-admin-immutable', 'The SuperAdmin is always an admin — that pairing is fixed and cannot be changed here.');
    }
    return ALLOW;
}
/**
 * Table row 4: deactivate or reactivate. Admin, never the SuperAdmin
 * *deactivated*, never yourself. Order matters: "never the SuperAdmin"
 * (deactivation only — see below) is checked first because it is true
 * regardless of who is asking — including the SuperAdmin deactivating
 * themselves, which must report the more specific `super-admin-protected`
 * reason rather than the generic lockout guard below.
 *
 * `requestedActive` is what makes the SuperAdmin branch DIRECTIONAL. Refusing
 * every `active` change on that row, both ways, was the original shape here —
 * and it had a real failure mode: a restored or hand-edited database can hold
 * `super_admin = true, active = false` (sign-in already refuses that
 * combination on its own, via staff.ts's `StaffDeactivatedError`), and a
 * blanket refusal here meant reactivation was refused too, with no route back
 * in short of an admin editing the row by hand. Deactivating the SuperAdmin
 * stays refused unconditionally; reactivating them is an ordinary admin
 * action, still gated below by the usual "never yourself" and "requires an
 * admin" checks.
 */
export function canChangeActive(actor, target, requestedActive) {
    if (target.superAdmin && !requestedActive) {
        return refuse('super-admin-protected', 'The SuperAdmin cannot be deactivated.');
    }
    // The lockout guard (design §3): an admin who deactivates their own account
    // cannot undo it, and on a two-person team that is a plausible mistake with an
    // expensive recovery. Checked ahead of the admin-floor check below so a
    // non-admin somehow reaching here on their own row gets the more specific
    // "not yourself" reason rather than a bare "not an admin".
    if (actor.id === target.id) {
        return refuse('self-lockout', 'You cannot deactivate your own account — an admin who locks themselves out has no way back in.');
    }
    if (!actor.admin) {
        return refuse('not-admin', 'Deactivating or reactivating a staff member requires an admin.');
    }
    return ALLOW;
}
/**
 * Table rows 2–4 — a single staff-row field change, dispatched to the
 * per-field function above. Row 5 (`super_admin`) has no case here; see this
 * file's header comment for why that is structural rather than a branch that
 * could be missed.
 *
 * The `switch` ends in an exhaustiveness check, the same discipline `gates.ts`
 * uses for its own operation switch: a fifth `PermChange` field fails to
 * compile here rather than silently falling through to "allowed".
 *
 * Only the `active` branch reads `value`, and it must: refusing every change
 * to a SuperAdmin's `active` in BOTH directions left a database holding
 * `super_admin = true, active = false` permanently locked out of its own
 * console — sign-in refuses a deactivated row, and reactivation refused too.
 * Deactivating a SuperAdmin is still refused unconditionally; only restoring
 * one is allowed. The other branches judge whether the actor may touch that
 * field on that target at all, regardless of the value.
 */
/**
 * Which 3CX extension a staff member sits at, so the live-call bar can tell
 * their calls from everyone else's.
 *
 * Any admin, on anyone — INCLUDING the SuperAdmin's row, unlike `role` and
 * `admin`. Those are locked down because they confer authority; an extension
 * confers nothing. It is a fact about where a person's phone is, and locking
 * the SuperAdmin's would leave exactly one person on the floor whose calls the
 * bar could never attribute, for no security gain.
 *
 * Deliberately not self-service for non-admins: the whole point of moving this
 * off a personal preference is that one admin sets the floor's extensions once
 * rather than seven people each being told to go and find a settings field.
 */
export function canChangeExtension(actor) {
    if (!actor.admin) {
        return refuse('not-admin', 'Setting a phone extension requires an admin.');
    }
    return ALLOW;
}
/**
 * Which Halo agent a staff member IS, so writes can be attributed to them.
 *
 * Admin-only, on the same reasoning as `canChangeExtension`: it is a fact about
 * the organisation, set once for the floor, not a personal preference seven
 * people each have to go and find.
 *
 * It is NOT locked down further, despite touching timesheets. What this value
 * does is put time on somebody's Halo timesheet — visible, auditable, and
 * corrigible in Halo by anyone who can already see it. What it cannot do is
 * claim a person PERFORMED an action: `actionby_agent_id` is pinned to the API
 * app's own agent and cannot be set by an API app at all (Hudu KB #1255). So an
 * admin who sets this wrongly misfiles time; they cannot forge authorship.
 */
export function canChangeHaloAgentName(actor) {
    if (!actor.admin) {
        return refuse('not-admin', 'Setting a Halo agent name requires an admin.');
    }
    return ALLOW;
}
export function checkStaffChange(actor, target, change) {
    switch (change.field) {
        case 'role':
            return canChangeRole(actor, target);
        case 'admin':
            return canChangeAdmin(actor, target);
        case 'active':
            return canChangeActive(actor, target, change.value);
        case 'ext':
            return canChangeExtension(actor);
        case 'haloAgentName':
            return canChangeHaloAgentName(actor);
        default: {
            const unhandled = change;
            throw new Error(`No permission rule exists for field "${String(unhandled.field)}".`);
        }
    }
}
